"use client";

import {
  Box,
  Button,
  Dialog,
  Typography,
  Grid,
  TextField,
  MenuItem,
  InputAdornment,
} from "@mui/material";
import type React from "react";
import { useEffect, useState } from "react";
import DynamicTable from "../../../../components/lab-dynamic-table";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useBloodBankDateQuery } from "../../blood-bank-date";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import PrintIcon from "@mui/icons-material/Print";
import { toast } from "react-toastify";
import DeleteConfirmationModal from "../../../../components/Modals/delete-modal";
import dayjs from "dayjs";
import { generateScreeningPDF } from "./generate-screening-pdf";
import { useSelector } from "react-redux";
import SearchIcon from "@mui/icons-material/Search";
import DateRangeSelector from "../../../../components/DateRangeSelector";

const BLOOD_GROUPS = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"];

const DonorHistory = () => {
  const [data, setData] = useState<any[]>([]);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [bloodGroup, setBloodGroup] = useState("");
  const [fitnessStatus, setFitnessStatus] = useState("");
  const [status, setStatus] = useState("");
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState("");
  const [editData, setEditData] = useState(null);
  const [open, setOpen] = useState(false);
  const [viewData, setViewData] = useState<any>();
  const [page, setPage] = useState(0);
  const {
    dateRange,
    setDateRange,
    fromDate,
    setFromDate,
    toDate,
    setToDate,
  } = useBloodBankDateQuery(setPage);
  const [total, setTotal] = useState<any>("");
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const navigate = useNavigate();
  const profile = useSelector((state: any) => state?.profileSlice?.profile);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(0);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const ScreeningData = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BLOOD_BANK}/donor-screening/findAllScreenings`,
        {
          params: {
            pageNo: page + 1,
            count: rowsPerPage,
            ...(fromDate ? { fromDate } : {}),
            ...(toDate ? { toDate } : {}),
            ...(search ? { search } : {}),
            ...(bloodGroup ? { bloodGroup } : {}),
            ...(fitnessStatus ? { fitnessStatus } : {}),
            ...(status ? { status } : {}),
          },
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      setData(response?.data?.data);
      setTotal(response?.data?.totalCount);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    ScreeningData();
  }, [page, rowsPerPage, search, fromDate, toDate, bloodGroup, fitnessStatus, status]);

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(Number.parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleEditClick = (id: any, data: any) => {
    setEditData(id);
    navigate("/donor-screening", { state: { idd: id, data: data } });
  };

  const handleDeleteModalClose = () => {
    setIsDeleteModalOpen(false);
  };

  const handleDeleteClick = (id: any) => {
    setIsDeleteModalOpen(true);
    setSelectedItemId(id);
  };

  const handleDeleteConfirm = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        console.error("Authorization token is missing");
        return;
      }
      const response = await axios.delete(
        `${process.env.REACT_APP_BLOOD_BANK}/donor-screening/deleteScreeningById/${selectedItemId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      ScreeningData();

      toast.success(response?.data?.message);
    } catch (error: any) {
      toast.error(error.message);
    }
    setIsDeleteModalOpen(false);
  };

  const handleViewClick = (data: any) => {
    setOpen(true);
    setViewData(data);
  };

  // Format date for PDF
  const formatDate = (dateString: string) => {
    if (!dateString) return "N/A";
    return dayjs(dateString).format("DD-MMM-YYYY");
  };

  // Handle print functionality
  const handlePrint = (rowIndex: number) => {
    const screeningData = data[rowIndex];
    if (!screeningData) {
      console.error("No screening data available");
      return;
    }

    // Get the performer name from profile
    const performerName =
      profile?.data?.fullName || profile?.data?.firstName || "";

    // Generate the PDF
    const doc = generateScreeningPDF(screeningData, formatDate, performerName);

    if (!doc) {
      toast.error("Failed to generate PDF");
      return;
    }

    // Create a blob from the PDF and print directly
    const pdfBlob = doc.output("blob");
    const blobUrl = URL.createObjectURL(pdfBlob);

    // Create an invisible iframe for printing
    const iframe = document.createElement("iframe");
    iframe.style.display = "none";
    document.body.appendChild(iframe);

    // Set up print handler
    iframe.onload = () => {
      try {
        // Add a small delay to ensure the PDF is fully loaded
        setTimeout(() => {
          if (iframe.contentWindow) {
            iframe.contentWindow.focus();
            iframe.contentWindow.print();

            // Only clean up after print dialog is closed
            iframe.contentWindow.onafterprint = () => {
              document.body.removeChild(iframe);
              URL.revokeObjectURL(blobUrl);
            };
          }
        }, 1000);
      } catch (e) {
        console.error("Error triggering print:", e);
        // Fallback if direct print fails
        window.open(blobUrl, "_blank");
        // Clean up the URL object
        URL.revokeObjectURL(blobUrl);
      }
    };

    // Set iframe source to the PDF blob URL
    iframe.src = blobUrl;

    // Backup cleanup in case onafterprint doesn't fire
    const backupCleanupTimer = setTimeout(() => {
      try {
        if (document.body.contains(iframe)) {
          document.body.removeChild(iframe);
          URL.revokeObjectURL(blobUrl);
        }
      } catch (e) {
        console.error("Error during backup cleanup:", e);
      }
    }, 300000); // 5 minutes
  };

  return (
    <Box>
      <Box sx={{ background: "#fff", p: 1, borderRadius: "8px" }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          gap={1}
          mb={1}
        >
          <Typography sx={{ fontSize: "16px", fontWeight: "bold" }}>
            Donors Screening Reports
          </Typography>
          <Button
            size="small"
            variant="contained"
            onClick={() => navigate("/donor-screening")}
            sx={{ fontSize: { md: "14px", sm: "12px", xs: "10px" } }}
          >
            Add Donor Screening
          </Button>
        </Box>
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <Box display="flex" alignItems="flex-end" flexWrap="wrap" gap={1.5}>
              <TextField
                className="custom-input"
                size="small"
                label="Search"
                placeholder="Donor ID or name"
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
                sx={{ width: { xs: "100%", sm: 200 } }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                className="custom-input"
                select
                size="small"
                label="Blood Group"
                value={bloodGroup}
                onChange={e => {
                  setBloodGroup(e.target.value);
                  setPage(0);
                }}
                sx={{ width: { xs: "100%", sm: 130 } }}
              >
                <MenuItem value="">All</MenuItem>
                {BLOOD_GROUPS.map(group => (
                  <MenuItem key={group} value={group}>
                    {group}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                className="custom-input"
                select
                size="small"
                label="Fitness"
                value={fitnessStatus}
                onChange={e => {
                  setFitnessStatus(e.target.value);
                  setPage(0);
                }}
                sx={{ width: { xs: "100%", sm: 130 } }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="fit">Fit</MenuItem>
                <MenuItem value="unfit">Unfit</MenuItem>
                <MenuItem value="Deferred">Deferred</MenuItem>
              </TextField>
              <TextField
                className="custom-input"
                select
                size="small"
                label="Status"
                value={status}
                onChange={e => {
                  setStatus(e.target.value);
                  setPage(0);
                }}
                sx={{ width: { xs: "100%", sm: 140 } }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="Accepted">Accepted</MenuItem>
                <MenuItem value="Deferred">Deferred</MenuItem>
                <MenuItem value="Not Bled">Not Bled</MenuItem>
              </TextField>
              <DateRangeSelector
                preset="blood"
                compact
                dateRange={dateRange}
                fromDate={fromDate}
                toDate={toDate}
                onDateRangeChange={value => {
                  setDateRange(value);
                  setPage(0);
                }}
                onFromDateChange={value => {
                  setFromDate(value);
                  setPage(0);
                }}
                onToDateChange={value => {
                  setToDate(value);
                  setPage(0);
                }}
              />
            </Box>
          </Grid>
        </Grid>
      </Box>

      <Box sx={{ background: "#fff", p: 0.5, mt: 1 }}>
        <DynamicTable
          headers={[
            "SN",
            "Donor ID",
            "Donor Name",
            "Blood Group",
            "Age",
            "hbsAg",
            "hcv",
            "hiv",
            "status",
            "Fitness Status",
            "Action",
          ]}
          data={data?.map((item: any, idx: any) => {
            return [
              idx + 1,
              item.donorId,
              item.donorData?.name,
              item.bloodGroup + item.rhFactor,
              item?.donorData?.age,
              item.hbsAg.result,
              item.hcv.result,
              item.hiv.result,
              item.status,
              item.fitnessStatus,
            ];
          })}
          loading={false}
          page={page}
          rowsPerPage={rowsPerPage}
          handleChangePage={handleChangePage}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
          totalRows={total}
          actionButtons={[
            {
              icon: <EditIcon sx={{ color: "#FFB800", cursor: "pointer" }} />,
              onClick: (rowIndex: any) =>
                handleEditClick(data[rowIndex]?._id, data[rowIndex]),
            },
            {
              icon: <DeleteIcon sx={{ color: "#FF5454", cursor: "pointer" }} />,
              onClick: (rowIndex: any) =>
                handleDeleteClick(data[rowIndex]?._id),
            },
            {
              icon: <PrintIcon sx={{ color: "green", cursor: "pointer" }} />,
              onClick: (rowIndex: any) => handlePrint(rowIndex),
            },
          ]}
        />
        {data === null && (
          <h1
            style={{
              color: "gray",
              marginTop: "8px",
              textAlign: "center",
              fontSize: "20px",
              fontWeight: "bold",
              marginBottom: "8px",
            }}
          >
            No data found
          </h1>
        )}
      </Box>

      <DeleteConfirmationModal
        open={isDeleteModalOpen}
        handleClose={handleDeleteModalClose}
        handleConfirm={handleDeleteConfirm}
      />
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="sm"
        fullWidth
      ></Dialog>
    </Box>
  );
};

export default DonorHistory;
